import * as GC from '@mescius/spread-sheets';
import { getCellTagSafely } from './cellTag';
import { collectLoopRanges, removeLoopById } from './loop';
import { removePropertyByCell } from './property';

type CellPosition = {
  row: number;
  col: number;
};

function collectPropertyCellPositions(
  sheet: GC.Spread.Sheets.Worksheet
): CellPosition[] {
  const usedRange = sheet.getUsedRange(GC.Spread.Sheets.UsedRangeType.tag);
  if (!usedRange) return [];

  const result: CellPosition[] = [];
  const visited = new Set<string>();

  const rowEnd = usedRange.row + usedRange.rowCount;
  const colEnd = usedRange.col + usedRange.colCount;

  for (let row = usedRange.row; row < rowEnd; row++) {
    for (let col = usedRange.col; col < colEnd; col++) {
      const tag = getCellTagSafely(sheet, row, col);
      if (!tag?.property) continue;

      const key = `${row}:${col}`;
      if (visited.has(key)) continue;

      visited.add(key);
      result.push({ row, col });
    }
  }

  return result;
}

function clearRemainingTags(sheet: GC.Spread.Sheets.Worksheet): void {
  const usedRange = sheet.getUsedRange(GC.Spread.Sheets.UsedRangeType.tag);
  if (!usedRange) return;

  const rowEnd = usedRange.row + usedRange.rowCount;
  const colEnd = usedRange.col + usedRange.colCount;

  for (let row = usedRange.row; row < rowEnd; row++) {
    for (let col = usedRange.col; col < colEnd; col++) {
      if (sheet.getTag(row, col) === undefined) continue;

      sheet.getCell(row, col).clear(GC.Spread.Sheets.StorageType.tag);
    }
  }
}

function sanitizeSheetForRequest(sheet: GC.Spread.Sheets.Worksheet): void {
  sheet.suspendPaint();

  try {
    const loopIds = collectLoopRanges(sheet).map((loop) => loop.loopId);

    for (const loopId of loopIds) {
      removeLoopById(sheet, loopId);
    }

    const propertyCells = collectPropertyCellPositions(sheet);

    for (const { row, col } of propertyCells) {
      removePropertyByCell(sheet, row, col);
    }

    clearRemainingTags(sheet);
  } finally {
    sheet.resumePaint();
  }
}

export function sanitizeWorkbookForRequest(
  workbook: GC.Spread.Sheets.Workbook
): GC.Spread.Sheets.Workbook {
  workbook.suspendPaint();

  try {
    for (
      let sheetIndex = 0;
      sheetIndex < workbook.getSheetCount();
      sheetIndex++
    ) {
      const sheet = workbook.getSheet(sheetIndex);
      sanitizeSheetForRequest(sheet);
    }

    return workbook;
  } finally {
    workbook.resumePaint();
  }
}

// excelの書き出し用のワークブックを作成
const createExportWorkbook = (src: GC.Spread.Sheets.Workbook) => {
  const json = src.toJSON({ includeBindingSource: true });

  // 表示しないオフスクリーン用Workbook
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '-10000px';
  host.style.width = '1px';
  host.style.height = '1px';
  document.body.appendChild(host);

  const exportSpread = new GC.Spread.Sheets.Workbook(host, {
    sheetCount: src.getSheetCount(),
  });
  exportSpread.fromJSON(json);

  return { exportSpread, host };
};

/**
 * SpreadJSワークブックからプロパティや明細範囲情報を削除してBase64化して返す
 */
export function exportExcelBase64(
  spread: GC.Spread.Sheets.Workbook
): Promise<string> {
  const { exportSpread, host } = createExportWorkbook(spread);

  const cleanup = () => {
    try {
      // Workbook に destroy があれば呼ぶ
      if (
        typeof (exportSpread as { destroy?: () => void }).destroy === 'function'
      ) {
        (exportSpread as { destroy: () => void }).destroy();
      }
    } finally {
      host.remove();
    }
  };

  try {
    sanitizeWorkbookForRequest(exportSpread);
  } catch (error) {
    cleanup();
    return Promise.reject(error);
  }

  return new Promise((resolve, reject) => {
    exportSpread.export(
      (blob: Blob) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          try {
            const base64 = reader.result as string;
            const pureBase64 = base64.split(',')[1];
            resolve(pureBase64);
          } catch (error) {
            reject(error);
          } finally {
            cleanup();
          }
        };
        reader.onerror = (error) => {
          cleanup();
          reject(error);
        };

        reader.readAsDataURL(blob);
      },
      (error: unknown) => {
        cleanup();
        reject(error);
      },
      {
        fileType: GC.Spread.Sheets.FileType.excel,
      }
    );
  });
}

/**
 * ワークブックからExcelファイルを作成しダウンロードする（検証用）
 */
export function downloadWorkbookAsExcel(
  spread: GC.Spread.Sheets.Workbook,
  fileName = 'spread.xlsx'
): void {
  spread.export((blob: Blob) => {
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  });
}
